// jobSheet.js — the INTERNAL job sheet print: the working details the client
// quote deliberately leaves out (estimator, hours, operatives, dry ice kg,
// machine, travel, internal notes). No prices, not saved, never sent to a
// client - printed from the calculator for the crew / job file.

const esc = v => String(v)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// sections: [{ title, rows: [[label, value], ...] }] - rows with an empty
// value are left off, and a section with no rows isn't printed.
// blocks: [{ title, text, html? }] free-text blocks (scope, notes), same rule;
// html (already escaped, e.g. scopeTextToHTML) is used as is when given.
export function buildJobSheetHTML({ heading, refNo, dateStr, sections = [], blocks = [] }) {
  const sectionHtml = sections.map(sec => {
    const rows = (sec.rows || []).filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "");
    if (!rows.length) return "";
    return `<div class="sec"><div class="sectitle">${esc(sec.title)}</div><table>${
      rows.map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td>${esc(v)}</td></tr>`).join("")
    }</table></div>`;
  }).join("");
  const blockHtml = blocks
    .filter(b => b.text && String(b.text).trim())
    .map(b => `<div class="sec"><div class="sectitle">${esc(b.title)}</div><div class="text">${b.html ?? esc(b.text).replace(/\n/g, "<br/>")}</div></div>`)
    .join("");

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Job Sheet ${esc(refNo)}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:-apple-system, 'IBM Plex Sans', sans-serif; color:#0e1520; font-size:12px; line-height:1.5; }
    .page { padding:36px 40px; max-width:794px; margin:0 auto; }
    .hdr { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:2px solid #0e1520; padding-bottom:10px; margin-bottom:18px; }
    h1 { font-size:20px; }
    .internal { font-size:10px; font-weight:700; letter-spacing:0.15em; text-transform:uppercase; color:#A93A36; }
    .ref { text-align:right; font-size:12px; color:#555; }
    .ref strong { font-size:15px; color:#0e1520; display:block; }
    .grid { display:grid; grid-template-columns:1fr 1fr; gap:4px 24px; }
    .sec { margin-bottom:14px; break-inside:avoid; }
    .sectitle { font-size:10px; font-weight:700; letter-spacing:0.15em; text-transform:uppercase; border-bottom:1px solid #dde2ea; padding-bottom:4px; margin-bottom:4px; }
    table { width:100%; border-collapse:collapse; }
    td { padding:3px 0; vertical-align:top; }
    td.k { width:42%; color:#5a6a7a; }
    .text { white-space:normal; }
    @media print { @page { margin:12mm; size:a4; } }
    /* On screen: a larger, readable sheet on a laptop or tablet. Print keeps
       the compact A4 layout above. */
    @media screen {
      body { background:#eef1f5; font-size:15px; line-height:1.55; }
      .page { max-width:900px; margin:24px auto; padding:40px 48px; background:#fff; border-radius:10px; box-shadow:0 2px 14px rgba(14,21,32,0.08); }
      h1 { font-size:24px; }
      .internal { font-size:11px; }
      .ref { font-size:14px; }
      .ref strong { font-size:18px; }
      .sectitle { font-size:11px; padding-bottom:6px; margin-bottom:6px; }
      td { padding:5px 0; }
      .grid { gap:8px 36px; }
      .sec { margin-bottom:20px; }
    }
    @media screen and (max-width:700px) {
      .page { margin:0; padding:20px 16px; border-radius:0; box-shadow:none; }
      .grid { grid-template-columns:1fr; }
      .hdr { flex-wrap:wrap; gap:8px; }
      .ref { text-align:left; }
    }
  </style>
</head>
<body>
<div class="page">
  <div class="hdr">
    <div><div class="internal">Internal job sheet · not for the client</div><h1>${esc(heading)}</h1></div>
    <div class="ref"><strong>${esc(refNo)}</strong>${esc(dateStr)}</div>
  </div>
  <div class="grid">${sectionHtml}</div>
  ${blockHtml}
</div>
</body>
</html>`;
}
